import React, { useState } from 'react';
import { Form, Row, Col, Button } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import type { BannerEditFormProps } from './types';
import { useBanner } from '../../../../../apps/hooks/banner/useBanner';

const BannerEditForm: React.FC<BannerEditFormProps> = ({ editingBanner, handleInputChange }) => {
  const banner = useBanner();
  const [isUploading, setIsUploading] = useState(false);
  const [dragOver, setDragOver] = useState<string | null>(null);

  const parseUTCDate = (utcDateString: string | null): Date | null => {
    if (!utcDateString) {
      return null;
    }
    const utcDate = new Date(utcDateString + (utcDateString.endsWith('Z') ? '' : 'Z'));
    return new Date(
      utcDate.getUTCFullYear(),
      utcDate.getUTCMonth(),
      utcDate.getUTCDate(),
      utcDate.getUTCHours(),
      utcDate.getUTCMinutes(),
    );
  };

  const formatDateToUTC = (localDate: Date): string => {
    const utcDate = new Date(
      Date.UTC(
        localDate.getFullYear(),
        localDate.getMonth(),
        localDate.getDate(),
        localDate.getHours(),
        localDate.getMinutes(),
      ),
    );
    return utcDate.toISOString();
  };
  if (!editingBanner) {
    return null;
  }

  // Handle file upload for media (image or video)
  const handleMediaUpload = async (file: File, mediaType: 'desktop' | 'tablet' | 'mobile') => {
    if (!file) {
      return;
    }

    setIsUploading(true);
    try {
      const mediaUrl = await banner.uploadFile(file);
      if (mediaUrl) {
        if (mediaType === 'desktop') {
          handleInputChange('imageUrl', mediaUrl);
        } else if (mediaType === 'tablet') {
          handleInputChange('tabletImageUrl', mediaUrl);
        } else if (mediaType === 'mobile') {
          handleInputChange('mobileImageUrl', mediaUrl);
        }
      }
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  // Handle file input change
  const handleFileInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    mediaType: 'desktop' | 'tablet' | 'mobile',
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      await handleMediaUpload(file, mediaType);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent, mediaType: string) => {
    e.preventDefault();
    setDragOver(mediaType);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(null);
  };

  const handleDrop = async (e: React.DragEvent, mediaType: 'desktop' | 'tablet' | 'mobile') => {
    e.preventDefault();
    setDragOver(null);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file && (file.type.startsWith('image/') || file.type.startsWith('video/'))) {
        await handleMediaUpload(file, mediaType);
      }
    }
  };

  // Media upload component
  const MediaUploadArea: React.FC<{
    mediaType: 'desktop' | 'tablet' | 'mobile';
    mediaUrl?: string | undefined;
    label: string;
    dimensions: string;
  }> = ({ mediaType, mediaUrl, label, dimensions }) => {
    // Check if the URL is a video file
    const isVideo = mediaUrl?.toLowerCase().endsWith('.mp4') || false;

    return (
      <div className="mb-3">
        <label className="form-label">
          {label} ({dimensions})
        </label>

        {mediaUrl ? (
          // Show media preview and remove button when media exists
          <div>
            {isVideo ? (
              <video
                src={mediaUrl}
                className="img-fluid mb-2"
                style={{ maxHeight: '100px' }}
                controls
                muted
                preload="metadata"
              />
            ) : (
              <img src={mediaUrl} alt={mediaType} className="img-fluid mb-2" style={{ maxHeight: '100px' }} />
            )}
            <div>
              <Button
                size="sm"
                variant="outline-danger"
                onClick={() =>
                  handleInputChange(
                    mediaType === 'desktop' ? 'imageUrl' : mediaType === 'tablet' ? 'tabletImageUrl' : 'mobileImageUrl',
                    '',
                  )
                }
              >
                Remove
              </Button>
            </div>
          </div>
        ) : (
          // Show drag & drop area when no media
          <>
            <div
              className={`border-3 border-dashed p-4 text-center rounded ${
                dragOver === mediaType
                  ? 'border-primary bg-primary bg-opacity-10 text-primary'
                  : 'border-info bg-light text-info'
              }`}
              style={{
                cursor: 'pointer',
                minHeight: '120px',
                transition: 'all 0.2s ease',
              }}
              onDragOver={(e) => handleDragOver(e, mediaType)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, mediaType)}
              onClick={() => document.getElementById(`file-input-${mediaType}`)?.click()}
            >
              {dragOver === mediaType ? (
                <div className="text-primary fw-bold">
                  <i className="bi bi-cloud-upload-fill" style={{ fontSize: '3rem' }}></i>
                  <div className="mt-2">Drop media here!</div>
                </div>
              ) : (
                <div>
                  <i className="bi bi-cloud-upload" style={{ fontSize: '2.5rem' }}></i>
                  <div className="mt-2 fw-semibold">Drag & drop image or video here</div>
                  <div className="text-muted small mt-1">or click to browse files</div>
                </div>
              )}
            </div>

            <input
              id={`file-input-${mediaType}`}
              type="file"
              className="d-none"
              accept="image/webp,video/mp4"
              onChange={(e) => handleFileInputChange(e, mediaType)}
              disabled={isUploading}
            />
          </>
        )}
      </div>
    );
  };

  return (
    <Form>
      <Form.Group className="mb-3">
        <Form.Label>Title</Form.Label>
        <Form.Control
          type="text"
          value={editingBanner.title}
          onChange={(e) => handleInputChange('title', e.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Description</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={editingBanner.description || ''}
          onChange={(e) => handleInputChange('description', e.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Banner Location</Form.Label>
        <Form.Control
          type="text"
          value={editingBanner.location || '/'}
          onChange={(e) => handleInputChange('location', e.target.value)}
          placeholder="/"
        />
        <Form.Text className="text-muted">
          Enter the path where this banner should be displayed (e.g., <code>/trend</code> for
          https://skin-seoul.com/trend)
        </Form.Text>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Link URL</Form.Label>
        <Form.Control
          type="url"
          value={editingBanner.linkUrl || ''}
          onChange={(e) => handleInputChange('linkUrl', e.target.value)}
        />
      </Form.Group>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>
              From Date <small className="text-muted">(UTC)</small>
            </Form.Label>
            <DatePicker
              selected={parseUTCDate(editingBanner.fromDate)}
              onChange={(date) => handleInputChange('fromDate', date ? formatDateToUTC(date) : null)}
              className="form-control"
              dateFormat="yyyy-MM-dd HH:mm"
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              timeCaption="Time (UTC)"
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>
              To Date <small className="text-muted">(UTC)</small>
            </Form.Label>
            <DatePicker
              selected={parseUTCDate(editingBanner.toDate)}
              onChange={(date) => handleInputChange('toDate', date ? formatDateToUTC(date) : null)}
              className="form-control"
              dateFormat="yyyy-MM-dd HH:mm"
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              timeCaption="Time (UTC)"
            />
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mb-3">
        <Form.Check
          type="checkbox"
          label="Active (Show this banner to visitors)"
          checked={editingBanner.active}
          onChange={(e) => handleInputChange('active', e.target.checked)}
          id="banner-active-checkbox"
        />
        <small className="text-muted">Server will automatically manage this based on date range.</small>
      </Form.Group>

      {/* Media Upload Section with Drag & Drop */}
      <div className="mb-4">
        <h5>Banner Media</h5>
        <Row>
          <Col md={4}>
            <MediaUploadArea
              mediaType="desktop"
              mediaUrl={editingBanner.imageUrl}
              label="Desktop Media"
              dimensions="1280×600"
            />
          </Col>

          <Col md={4}>
            <MediaUploadArea
              mediaType="tablet"
              mediaUrl={editingBanner.tabletImageUrl}
              label="Tablet Media"
              dimensions="1170×400"
            />
          </Col>

          <Col md={4}>
            <MediaUploadArea
              mediaType="mobile"
              mediaUrl={editingBanner.mobileImageUrl}
              label="Mobile Media"
              dimensions="360×390"
            />
          </Col>
        </Row>
        {isUploading && <div className="text-info">Uploading...</div>}
      </div>
    </Form>
  );
};

export default BannerEditForm;
